/**
 * Links from a BI dashboard to Advanced (BI) (CU-869fmhmx8 D9): the whole BI form, encoded with BI's own encoder,
 * with `run=1` so the explorer opens already searched. Total price (`isDailyPrice: false`) as the dashboard shows.
 * The explorer only knows "the same date last year": with the same weekday it gets `{year, 1}` and the toast says so.
 */
import { encodeSpParam } from '../../../../@utils/spParam.js'
import { fmtDay, fmtRange, fromIso, toCompact } from './dates.js'
import { CARS, carsOn } from './cars.js'

export const PERIOD_COMP = Object.freeze({
	ly: { type: 'year', quantity: 1 },
	lw: { type: 'day', quantity: 7 },
	yday: { type: 'day', quantity: 1 },
})

// CU-869fmjzrg: the prices of a capture day and a provider's detail open provider × Acriss for those captures
const REPORT = { view: 'provider-pickup', cell: 'provider-pickup', curve: 'provider-booking', day: 'acriss-provider', provider: 'acriss-provider' }
const BY_DAY = ['curve', 'day', 'provider']

const asDate = d => (d instanceof Date ? d : fromIso(d))

/**
 * The cars of the view as BI's form says them (E5): Advanced (BI) has no groups, so a group travels as the codes the
 * server resolved (`resolved` = the answer's `cars`), never as the client's list.
 */
export function explorerCars({ view, resolved = null }) {
	if (!carsOn(view)) return { acriss: [], categoryID: [] }
	if (view.cars === CARS.GROUP) return { acriss: [...(resolved?.codes || [])], categoryID: [] }
	if (view.cars === CARS.CATEGORY) return { acriss: [], categoryID: [...view.carsIds] }
	return { acriss: [...view.carsIds], categoryID: [] }
}

/**
 * BI's form (`BIFormData`) for a view of the dashboard.
 *  - `kind: 'view'` → provider × pick-up, every pick-up of the view;
 *  - `kind: 'cell'` → provider × pick-up, the pick-ups of the column;
 *  - `kind: 'curve'` → provider × booking (capture) date, the pick-ups of the column, by day.
 *
 * Filters (E5): the cars of `explorerCars`; `providerIds` = the providers shown with a market filter (your brand and
 * your other brands shown included, or Advanced (BI) would leave your row out), `null`/empty = everyone. The tier is
 * never sent: it already travels expanded in `providerID`.
 */
export function explorerForm({ kind = 'view', view, pickup, capture, currency, columnDates = null, resolvedCars = null, providerIds = null }) {
	const dates = kind === 'view' || !columnDates?.length ? [pickup.from, pickup.to] : [columnDates[0], columnDates[columnDates.length - 1]]
	return {
		siteGroupID: null,
		siteID: [],
		officePickupID: view.office ? [view.office] : [],
		officeGroupID: null,
		officeDropoffID: null,
		startPickupDate: toCompact(asDate(dates[0])),
		endPickupDate: toCompact(asDate(dates[1])),
		startBookingDate: toCompact(asDate(capture.from)),
		endBookingDate: toCompact(asDate(capture.to)),
		lor: [String(view.lor)],
		currency: currency ?? null,
		isPreviousComparison: true,
		periodComp: { ...PERIOD_COMP[view.cmp] },
		reportType: REPORT[kind] || REPORT.view,
		tiers: null,
		providerID: providerIds?.length ? [...providerIds].map(Number).sort((a, b) => a - b) : [],
		webSupplierID: [],
		...explorerCars({ view, resolved: resolvedCars }),
		unlimited_km: null,
		insurance: null,
		is_prepaid: null,
		fuelPolicyID: null,
		automatic: null,
		in_terminal_pickup: null,
		isDailyPrice: false,
		groupBy: BY_DAY.includes(kind) ? 'day' : view.by,
		isNewVersion: true,
		urlId: null,
		officePickupIDAux: null,
	}
}

/** `<BI path>?sp=…&run=1` */
export function explorerUrl(form, path) {
	return `${path}?sp=${encodeSpParam(form)}&run=1`
}

const STAT_LABEL = { min: 'Min', avg: 'Avg', max: 'Max' }
const COMPARE_TEXT = { ly: '1 year before', lw: '7 days before', yday: '1 day before' }

/** The toast that says what opens: `{ title, lines }` */
export function explorerToast({ kind = 'view', view, pickup, columnDates = null, officeName, explorerTitle, resolvedCars = null, market = null }) {
	const range = kind === 'view' || !columnDates?.length
		? fmtRange(asDate(pickup.from), asDate(pickup.to))
		: columnDates.length > 1 ? fmtRange(asDate(columnDates[0]), asDate(columnDates[columnDates.length - 1])) : fmtDay(asDate(columnDates[0]))
	const parts = kind === 'curve'
		? ['Provider × booking date', `pick-ups ${range}`, officeName, `${view.lor} days`, `compare ${COMPARE_TEXT[view.cmp]}`]
		: kind === 'cell'
			? ['Provider × pick-up', range, officeName, `${view.lor} days`, STAT_LABEL[view.stat], `compare ${COMPARE_TEXT[view.cmp]}`]
			: ['Provider × pick-up', range, officeName, `${view.lor} days`, `by ${view.by}`, STAT_LABEL[view.stat], `compare ${COMPARE_TEXT[view.cmp]}`]
	const lines = [parts.filter(Boolean).join(' · '), ...filterLines({ view, resolvedCars, market, explorerTitle })]
	if (view.cmp === 'ly' && !view.same) {
		lines.push(`${explorerTitle} compares with the same date last year, so its last-year figures can differ slightly from the same-weekday ones.`)
	}
	return { title: `Opening ${explorerTitle}`, lines }
}

/**
 * The lines of the filters in the toast (UX): the cars, and the providers shown + your brand. `market` =
 * `{ names: [shown, your brand aside], you: <brand name> | null }` when a market filter is on.
 */
export function filterLines({ view, resolvedCars = null, market = null, explorerTitle = 'Advanced (BI)' }) {
	const lines = []
	if (carsOn(view)) {
		const { acriss, categoryID } = explorerCars({ view, resolved: resolvedCars })
		if (view.cars === CARS.GROUP) lines.push(`Acriss: ${acriss.join(', ')} — ${explorerTitle} has no Acriss groups, so it gets their codes.`)
		else if (view.cars === CARS.CATEGORY) lines.push(`Categories: ${categoryID.join(', ')}`)
		else lines.push(`Acriss: ${acriss.join(', ')}`)
	}
	if (market) lines.push(`Providers: ${market.names.join(', ')}${market.you ? ` + ${market.you} (you)` : ''}`)
	return lines
}

/**
 * "My office network" → Advanced (BI) (CU-869fmjzrh D16). The offices go as the ids of the answer, never a group (Season
 * C3); totals for the rental (`isDailyPrice: false`, as Season D9).
 *  - `kind: 'network'` → provider × office, every office and length shown;
 *  - `kind: 'networkLength'` → provider × office, one office and one length;
 *  - `kind: 'networkPickup'` → provider × pick-up, one office, one length, the column's pick-ups.
 * `compare`: `day` · `week` · `year` · `yearSame` (Advanced (BI) only knows "the same date last year").
 */
export const NETWORK_PERIOD_COMP = Object.freeze({
	day: { type: 'day', quantity: 1 }, week: { type: 'day', quantity: 7 },
	year: { type: 'year', quantity: 1 }, yearSame: { type: 'year', quantity: 1 },
})

export function networkExplorerForm({ kind = 'network', view, officeIds = [], lengths = [], siteIds = [], pickup, capture, currency, columnDates = null, resolvedCars = null, providerIds = null }) {
	const dates = kind !== 'networkPickup' || !columnDates?.length ? [pickup.from, pickup.to] : [columnDates[0], columnDates[columnDates.length - 1]]
	return {
		siteGroupID: null,
		siteID: [...siteIds].map(Number),
		officePickupID: [...officeIds].map(Number),
		officeGroupID: null,
		officeDropoffID: null,
		startPickupDate: toCompact(asDate(dates[0])),
		endPickupDate: toCompact(asDate(dates[1])),
		startBookingDate: toCompact(asDate(capture.from)),
		endBookingDate: toCompact(asDate(capture.to)),
		lor: [...lengths].map(String),
		currency: currency ?? null,
		isPreviousComparison: true,
		periodComp: { ...NETWORK_PERIOD_COMP[view.cmp] },
		reportType: kind === 'networkPickup' ? 'provider-pickup' : 'provider-office',
		tiers: null,
		providerID: providerIds?.length ? [...providerIds].map(Number).sort((a, b) => a - b) : [],
		webSupplierID: [],
		...explorerCars({ view, resolved: resolvedCars }),
		unlimited_km: null,
		insurance: null,
		is_prepaid: null,
		fuelPolicyID: null,
		automatic: null,
		in_terminal_pickup: null,
		isDailyPrice: false,
		groupBy: kind === 'networkPickup' && view.by !== 'day' ? 'week' : 'day',
		isNewVersion: true,
		urlId: null,
		officePickupIDAux: null,
	}
}
